import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {
  BalansMVA,
  BokkzVpbDataService,
  CorporateTaxEntity,
} from "@snelstart-ventures/fiscaal/domain-logic";
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {debounceTime, filter, Observable, shareReplay, tap} from "rxjs";
import {SharedUiDialogModule} from '@snelstart/ui-dialog';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {AsyncPipe, DatePipe} from '@angular/common';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

@Component({
  selector: 'snelstart-venture-fiscaal-feature-spec-machines-en-installaties',
  templateUrl: './spec-machines-en-installaties.component.html',
  styleUrls: ['./spec-machines-en-installaties.scss'],
  imports: [
    SharedUiDialogModule,
    ReactiveFormsModule,
    SharedUiDetailsModule,
    SharedUiFormControlsModule,
    SharedUiButtonModule,
    DatePipe,
    AsyncPipe
  ]
})
export class SpecMachinesEnInstallatiesComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private dataService = inject(BokkzVpbDataService);
  private destroyRef = inject(DestroyRef);
  public dialogVisible = false;

  readonly corporateTaxEntity$: Observable<CorporateTaxEntity> =
    this.dataService.corporateTaxEntity$.pipe(
      filter((data): data is CorporateTaxEntity => !!data?.sstRelationId),
      tap(data => {
        if (data.aangifte?.balansEnResultatenrekening?.machinesEnInstallaties?.at(0)) {
          this.machinesEnInstallatiesFg.patchValue(
            data.aangifte.balansEnResultatenrekening.machinesEnInstallaties[0],
            {emitEvent: false}
          );
        }
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  public machinesEnInstallatiesFg = new FormGroup({
    omschrijving: new FormControl<string | null>(null),
    openingsbalans: new FormControl<number | null>(null),
    slotbalans: new FormControl<number | null>(null),
    nominaleWaardeOfKostenprijs: new FormControl<number | null>(null, {
      nonNullable: true,
      validators: [
        Validators.required,
      ],
    }),
    restwaarde: new FormControl<number | null>(null, {
      nonNullable: true,
      validators: [
        Validators.required,
      ],
    })
  })

  showDialog() {
    this.dialogVisible = true;
  }

  ngOnInit(): void {
    this.corporateTaxEntity$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe();

    this.machinesEnInstallatiesFg.valueChanges.pipe(
      debounceTime(500),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.saveForm();
    });
  }

  ngOnDestroy(): void {
    this.saveForm();
  }

  saveForm(): void {
    this.machinesEnInstallatiesFg.updateValueAndValidity({emitEvent: false});

    const formValue = this.machinesEnInstallatiesFg.getRawValue();
    const specificatie: BalansMVA = {
      omschrijving: formValue.omschrijving?.trim() || undefined,
      openingsbalans: formValue.openingsbalans ?? undefined,
      slotbalans: formValue.slotbalans ?? undefined,
      nominaleWaardeOfKostenprijs: formValue.nominaleWaardeOfKostenprijs ?? undefined,
      restwaarde: formValue.restwaarde ?? undefined,
    };

    this.dataService.patchCorporateTaxEntity(currentEntity => {
      if (!currentEntity.aangifte) {
        return currentEntity;
      }

      const huidigeSpecificatie = currentEntity.aangifte.balansEnResultatenrekening?.machinesEnInstallaties?.[0];

      const mergedSpecificatie: BalansMVA = {
        ...huidigeSpecificatie,
        ...specificatie,
        // Preserve the main balance values when only the detailed specification is edited.
        openingsbalans: specificatie.openingsbalans ?? huidigeSpecificatie?.openingsbalans,
        slotbalans: specificatie.slotbalans ?? huidigeSpecificatie?.slotbalans,
      };

      const hasData = Object.values(mergedSpecificatie).some(value => value !== undefined);

      return {
        ...currentEntity,
        aangifte: {
          ...currentEntity.aangifte,
          balansEnResultatenrekening: {
            ...currentEntity.aangifte.balansEnResultatenrekening,
            machinesEnInstallaties: hasData ? [mergedSpecificatie] : undefined,
          },
        },
      } as CorporateTaxEntity;
    });
  }
}
